import {
  IssuesCloseOutlined,
  CheckOutlined,
  ThunderboltFilled,
  MergeOutlined,
  SignatureOutlined,
  WarningOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { LunchTag } from "../../ordering/lunch/LunchTag";
import { useTranslation } from "react-i18next";
import { isCommentModifier, type FlatProduct } from "../types";
import { CrossStationArrowIcon, crossStationLabelOf } from "./crossStation";
import StationPartTag from "./StationPartTag";

// Един продуктов ред в списъчния изглед на мониторите (аналог на ProductItem
// за решетката): основният grid ред + под-редовете с модификатори.

const StatusIcon = ({
  status,
  isSplit,
  isCancelled,
  isFullyCompleted,
  isOverdue,
}: {
  status: FlatProduct["status"];
  isSplit: boolean;
  isCancelled: boolean;
  isFullyCompleted: boolean;
  isOverdue: boolean;
}) => {
  if (isSplit) return <MergeOutlined className="text-[18px] text-muted" />;
  if (isCancelled)
    return <SignatureOutlined className="text-[18px] text-muted" />;
  // Издадено от шпикера — синя „изпратено" иконка, преди общия зелен чек.
  if (status === "3")
    return <SendOutlined className="text-[19px] text-info" />;
  const tint = isOverdue;
  if (isFullyCompleted)
    return (
      <CheckOutlined
        className={
          tint ? "text-[19px] text-danger-ink" : "text-[19px] text-ok"
        }
      />
    );
  switch (status) {
    case "0":
      return (
        <IssuesCloseOutlined
          className={
            tint ? "text-[18px] text-danger-ink" : "text-[18px] text-muted"
          }
        />
      );
    case "1":
      return (
        <ThunderboltFilled
          className={
            tint ? "text-[19px] text-danger-ink" : "text-[19px] text-brand-ink"
          }
        />
      );
    case "2":
      return (
        <CheckOutlined
          className={
            tint ? "text-[19px] text-danger-ink" : "text-[19px] text-ok"
          }
        />
      );
    default:
      return null;
  }
};

// Визуализация на клон в дървото за sub-редовете (модификатор / добавка).
// `depth` 1 = директен наследник на основен продукт, 2 = наследник на добавка.
// `isLast` отрязва релсата на средата и добавя завой + стрелка вместо пълна вертикала.
// `throughRail` = true рисува и родителската релса (depth-1), която "минава през"
// този ред надолу към следващата добавка.
// Геометрия (литерали в класовете/пътищата): отстъп 32px на ниво; релсата е на
// x=8 (ниво 2 → x=40), 2px, rgba(17,17,17,0.28); elbow svg-то е 20×20 с
// хоризонтално рамо на y=15 (извивката почва от y=6), branch svg-то е 20×10 с
// рамо на y=5. Релсата (div) свършва ТОЧНО където почва SVG пътят — цветът е
// полупрозрачен и всяко застъпване се вижда като по-тъмен/дебел участък.
const TreeBranch = ({
  depth,
  isLast,
  throughRail,
}: {
  depth: 1 | 2;
  isLast: boolean;
  throughRail: boolean;
}) => (
  <div
    className={`relative shrink-0 self-stretch ${
      depth === 1 ? "w-[32px]" : "w-[64px]"
    }`}
    aria-hidden
  >
    {throughRail && (
      <div className="absolute bottom-0 left-[8px] top-0 w-[2px] bg-shade-5" />
    )}
    <div
      className={`absolute top-0 w-[2px] bg-shade-5 ${
        depth === 1 ? "left-[8px]" : "left-[40px]"
      } ${isLast ? "h-[calc(50%-15px)]" : "h-full"}`}
    />
    {isLast ? (
      <svg
        width={20}
        height={20}
        viewBox="0 0 20 20"
        fill="none"
        className={`absolute top-[calc(50%-15px)] ${
          depth === 1 ? "left-[8px]" : "left-[40px]"
        }`}
      >
        <path
          d="M1 0 V6 Q1 15 10 15 H15.5"
          className="stroke-shade-5 stroke-2"
          strokeLinecap="butt"
          fill="none"
        />
        <path
          d="M12.5 11.5 L16 15 L12.5 18.5"
          className="stroke-shade-5 stroke-2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    ) : (
      <svg
        width={20}
        height={10}
        viewBox="0 0 20 10"
        fill="none"
        className={`absolute top-[calc(50%-5px)] ${
          depth === 1 ? "left-[8px]" : "left-[40px]"
        }`}
      >
        {/* Рамото тръгва от десния ръб на релсата (x=2), не от центъра ѝ —
            иначе кръстосването с вертикала потъмнява */}
        <path
          d="M2 5 H15.5"
          className="stroke-shade-5 stroke-2"
          strokeLinecap="butt"
          fill="none"
        />
        <path
          d="M12.5 1.5 L16 5 L12.5 8.5"
          className="stroke-shade-5 stroke-2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    )}
  </div>
);

interface ProductRowProps {
  product: FlatProduct;
  // Общият grid template на списъка — динамичен (настройки/данни), затова
  // се подава от MonitorsListView и стои като inline style.
  gridTemplate: string;
  isSelected: boolean;
  // Фонът се извежда от настройката „Оцветяване" в MonitorsListView;
  // undefined при „Без оцветяване".
  rowBackground: string | undefined;
  // Tree контекст, изчислен върху целия списък (виж treeMetaByIndex).
  isLastAdditionInGroup: boolean;
  mainHasAdditions: boolean;
  aggregatedQuantities: boolean;
  aggregatedTotal?: number;
  // Кои статуси значат „изпълнено" на този екран ("2" обикновен, "3" шпикер).
  fulfilledStatuses: string[];
  // null = поръчката е изпълнена и брояч не се показва.
  elapsed: string | null;
  orderTimeLabel: string;
  onClick: () => void;
  rowRef: (el: HTMLDivElement | null) => void;
}

const ProductRow = ({
  product,
  gridTemplate,
  isSelected,
  rowBackground,
  isLastAdditionInGroup,
  mainHasAdditions,
  aggregatedQuantities,
  aggregatedTotal,
  fulfilledStatuses,
  elapsed,
  orderTimeLabel,
  onClick,
  rowRef,
}: ProductRowProps) => {
  const { t } = useTranslation();

  const isInert = product.isSplit || product.isCancelled;
  const isFullyCompleted =
    fulfilledStatuses.includes(product.status) ||
    (product.quantity > 1 &&
      product.completedQuantity >= product.quantity);
  const showProgress =
    product.quantity > 1 &&
    product.completedQuantity > 0 &&
    product.completedQuantity < product.quantity;

  const tableLabel = product.oldTableName
    ? `#${product.oldTableName} » ${product.tableName}`
    : `#${product.tableName}`;

  const hasModifiers = !!product.modifiers && product.modifiers.length > 0;
  // „Общо" се показва само при НАИСТИНА събрани няколко реда — единичен ред
  // би дублирал количеството си като шум (виж същото правило в ProductItem)
  const showAggregatedValue =
    aggregatedQuantities &&
    aggregatedTotal != null &&
    aggregatedTotal > product.quantity &&
    !isInert;

  const struck = isInert ? "line-through" : "";

  // Подтекст „(към Бургер от Топла Кухня)" — само при добавка без
  // видим основен продукт на този монитор (виж crossStation.tsx).
  const crossStationLabel = crossStationLabelOf(
    t,
    product.isAddition,
    product.mainProductName,
    product.mainStationName,
  );

  return (
    <div
      className={`relative border-b border-shade-1 transition-[box-shadow,background] duration-[140ms] ${
        isSelected
          ? "z-[1] shadow-[inset_3px_0_0_0_#D97706,0_1px_2px_rgba(17,17,17,0.04),0_0_0_1px_rgba(17,17,17,0.05)]"
          : "z-0"
      }`}
      // Цветът на реда се извежда от името на масата (hash) — стойност,
      // известна чак по време на изпълнение
      style={{
        background: isSelected
          ? "var(--color-surface)"
          : rowBackground ?? "var(--color-surface)",
      }}
    >
      <div
        ref={rowRef}
        onClick={(clickEvent) => {
          // Не стига до фона (той сваля селекцията); inert редът не селектира
          clickEvent.stopPropagation();
          if (!isInert) onClick();
        }}
        className={`grid min-h-[44px] scroll-mt-[44px] scroll-mb-[8px] items-center gap-x-[10px] py-[8px] pl-[18px] pr-[14px] ${
          isInert ? "cursor-default opacity-55" : "cursor-pointer"
        }`}
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {/* Status */}
        <div className="flex items-center justify-center">
          <StatusIcon
            status={product.status}
            isSplit={product.isSplit}
            isCancelled={product.isCancelled}
            isFullyCompleted={isFullyCompleted}
            isOverdue={product.isOverdue}
          />
        </div>

        {/* Table */}
        <div
          className={`truncate text-[15.5px] font-bold tracking-[-0.2px] text-ink ${struck}`}
        >
          {tableLabel}
        </div>

        {/* Product + unitAndSize (+ tree-branch indent at depth 1 for additions) */}
        <div className="flex min-w-0 items-stretch">
          {/* Клонът има смисъл само при видим родител отгоре; при
              cross-station „сираче" контекстът е подтекстът „(към …)" */}
          {product.isAddition && !crossStationLabel && (
            <TreeBranch
              depth={1}
              isLast={isLastAdditionInGroup}
              throughRail={false}
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-center">
            <div className="flex min-w-0 items-center gap-[8px]">
              <span
                className={`min-w-0 truncate text-[16px] font-semibold tracking-[-0.15px] text-ink ${struck}`}
              >
                {product.productName}
              </span>
              {product.isLunch && <LunchTag />}
              {product.unitAndSize && (
                <span
                  className={`shrink-0 whitespace-nowrap text-[13px] font-medium tabular-nums text-muted ${struck}`}
                >
                  {product.unitAndSize}
                </span>
              )}
              {product.stationTag && (
                <StationPartTag name={product.stationTag} />
              )}
            </div>
            {(crossStationLabel ?? product.stationHint) && (
              <span className="flex min-w-0 items-center gap-[5px]">
                <CrossStationArrowIcon />
                <span className="min-w-0 truncate text-[12px] font-medium leading-[16px] text-muted">
                  {crossStationLabel ?? product.stationHint}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Elapsed */}
        <div
          className={`whitespace-nowrap text-right font-['JetBrains_Mono',ui-monospace,monospace] text-[14px] tracking-[0.3px] tabular-nums ${
            product.isOverdue
              ? "font-bold text-danger-ink"
              : "font-medium text-muted"
          }`}
        >
          {elapsed ?? "—"}
        </div>

        {/* Qty */}
        <div
          className={`whitespace-nowrap text-right text-[15.5px] font-bold tracking-[-0.1px] tabular-nums text-ink ${struck}`}
        >
          {showProgress
            ? `${product.completedQuantity} / ${product.quantity} ${t("monitors.pcs")}`
            : `${product.quantity} ${t("monitors.pcs")}`}
        </div>

        {/* Aggregated total (optional) */}
        {aggregatedQuantities && (
          <div className="whitespace-nowrap text-center text-[14px] font-semibold tabular-nums text-muted">
            {showAggregatedValue ? aggregatedTotal : "—"}
          </div>
        )}

        {/* Operator */}
        <div className="truncate text-[14px] font-semibold text-body">
          {product.waiterName}
        </div>

        {/* Order time */}
        <div className="whitespace-nowrap text-right font-['JetBrains_Mono',ui-monospace,monospace] text-[14px] font-medium tracking-[0.3px] tabular-nums text-muted">
          {orderTimeLabel}
        </div>
      </div>

      {/* Modifiers sub-rows (без разделителни линии — част от същия артикул) */}
      {hasModifiers &&
        product.modifiers!.map((mod, modIdx) => {
          const isLastMod = modIdx === product.modifiers!.length - 1;
          // Дълбочина: модификаторите на основен продукт са на ниво 1,
          // на добавка — на ниво 2 (с "пропускаща" релса на ниво 1 само
          // ако има следваща добавка надолу).
          const modDepth: 1 | 2 = product.isAddition ? 2 : 1;
          const modThroughRail = Boolean(
            product.isAddition && !isLastAdditionInGroup,
          );
          // isLast за собствената релса на модификатора:
          // - При основен продукт без добавки → последният мод е изключителен лист.
          // - При основен продукт с добавки → релсата минава надолу, isLast=false.
          // - При добавка → isLast когато е последен мод в тази добавка.
          const modIsLast = product.isAddition
            ? isLastMod
            : isLastMod && !mainHasAdditions;
          return (
            <div
              key={`mod-${mod.id}`}
              className="grid min-h-[26px] items-stretch gap-x-[10px] pb-[2px] pl-[18px] pr-[14px] pt-[1px]"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div />
              <div />
              <div className="flex min-w-0 items-stretch">
                <TreeBranch
                  depth={modDepth}
                  isLast={modIsLast}
                  throughRail={modThroughRail}
                />
                <div
                  className={`flex min-w-0 flex-1 items-center gap-[8px] overflow-hidden text-ellipsis whitespace-nowrap text-[14px] text-body ${
                    isCommentModifier(mod.id) ? "italic" : ""
                  }`}
                >
                  <WarningOutlined className="shrink-0 text-[13.5px] text-danger-ink" />
                  <span className="overflow-hidden text-ellipsis">
                    {mod.name}
                  </span>
                </div>
              </div>
              <div />
              <div />
              {aggregatedQuantities && <div />}
              <div />
              <div />
            </div>
          );
        })}
    </div>
  );
};

export default ProductRow;
